import { useMemo, useState, useCallback } from 'react'
import { OrderUpdateModalCss } from './OrderUpdateModalCss.styled'
import { ASSETS } from 'constants/assets'
import { currencyFormatter } from 'utils'
import { usePriceFeed } from 'hooks/usePriceFeed'
import { useLvgOrderManager } from 'hooks/games/lvg/useYourBidsOrders'
import { TrailingEdit } from './TrailingEdit'
import { TakeProfitEdit } from './TakeProfitEdit'
import { StopLossEdit } from './StopLossEdit'

export const OrderUpdateModal = ({ closeModal, order }) => {
  // const { asset, avatar, address,bustPrice, createdAt, entryPrice, amount, leverage, side, username } = order
  const {
    asset,
    entryPrice,
    side,
  } = order

  // const { updateModal } = useYoloModal()
  const { updateOrder } = useLvgOrderManager()

  const assetInfo = ASSETS.filter((item) => {
    return item.id === asset
  })[0]

  const { priceFeed } = usePriceFeed(assetInfo.id)
  const currentPrice = useMemo(() => priceFeed.price, [priceFeed?.price])
  
  //Take Profit Form management
  const [newTakeProfitProps, setNewTakeProfitProps] = useState({})
  const [newStopLossProps, setNewStopLossProps] = useState({})
  const [newTrailingProps, setNewTrailingProps] = useState({})
  
  const [takeProfitError, setTakeProfitError] = useState('')
  const checkTakeProfitError = useCallback((props) => {
    if (!props.newTakeProfitPrice) {
      setTakeProfitError('')
      return 
    }
    if (side === 'buy' && Number(props.newTakeProfitPrice) < Number(entryPrice)) {
      setTakeProfitError(
        `The new value should be above ${currencyFormatter(entryPrice, {
          noCurrencySign: false,
          decimalDigits: assetInfo.fiatDecimals,
        })}`
        )
        return 
      }
      if (side === 'sell' && Number(props.newTakeProfitPrice) > Number(entryPrice)) {
        setTakeProfitError(
          `The new value should be below ${currencyFormatter(entryPrice, {
            noCurrencySign: false,
            decimalDigits: assetInfo.fiatDecimals,
          })}`
          )
          return 
        }
        
        setTakeProfitError('')
      }, [assetInfo?.fiatDecimals, entryPrice, side])
      
      const [stopLossError, setStopLossError] = useState('')
    const checkStopLossError = useCallback((props) => {
    if (side === 'buy' && Number(props.newUserStopLossPrice) > Number(currentPrice)) {
      setStopLossError(
        `The new value should be below ${currencyFormatter(currentPrice, {
          noCurrencySign: false,
          decimalDigits: assetInfo.fiatDecimals,
        })}`
      )
      return 
    }

    if (side === 'sell' && Number(props.newUserStopLossPrice) < Number(currentPrice)) {
      setStopLossError(
        `The new value should be above ${currencyFormatter(currentPrice, {
          noCurrencySign: false,
          decimalDigits: assetInfo.fiatDecimals,
        })}`
      )
      return 
    }
        setStopLossError('')
      
  }, [assetInfo?.fiatDecimals, currentPrice, side])

  const onNewTakeProfitPropsChange = (props) => {
    checkTakeProfitError(props)
    const newProps = {
      newTakeProfitPrice: Number(props.newTakeProfitPrice) || undefined,
      newTakeProfitValue: Number(props.newTakeProfitValue) || undefined,
      cancelTakeProfitOrder: props.cancelTakeProfitOrder || false,
    }
    setNewTakeProfitProps(newProps)
  }
  
  const onNewStopLossPropsChange = (props) => {
    checkStopLossError(props)
    const newProps = {
      newUserStopLossPrice:    Number(props.newUserStopLossPrice) || undefined,
      newUserStopLossValue:    Number(props.newUserStopLossValue) || undefined,
      cancelUserStopLossOrder: props.cancelUserStopLossOrder || false,
    }
    setNewStopLossProps(newProps)
  }
  
  const setTrailingProps = (trailingProps) => {
    const newProps = {
      newTrailingStopPercent: Number(trailingProps.newTrailingStopPercent) || undefined,
      newTrailingStopValue: Number(trailingProps.newTrailingStopValue) || undefined,
      cancelTrailingStopOrder: trailingProps.cancelTrailingStopOrder || false,
    }
    setNewTrailingProps(newProps)
  }

  const disabledSaveChanges = useMemo(() => {
    if (!takeProfitError) {
      if (
        newTakeProfitProps?.newTakeProfitPrice ||
        newTakeProfitProps?.newTakeProfitValue ||
        newTakeProfitProps?.cancelTakeProfitOrder
      )
        return false
    }
    if (!stopLossError) {
      if (
        newStopLossProps?.newUserStopLossPrice ||
        newStopLossProps?.newUserStopLossValue ||
        newStopLossProps?.cancelUserStopLossOrder
      )
        return false
    }
    
    if (
      newTrailingProps?.newTrailingStopValue ||
      newTrailingProps?.newTrailingStopPercent ||
      newTrailingProps?.cancelTrailingStopOrder
    )
      return false

    return true
  }, [
    takeProfitError,
    newTakeProfitProps?.newTakeProfitPrice,
    newTakeProfitProps?.newTakeProfitValue,
    newTakeProfitProps?.cancelTakeProfitOrder,
    
    stopLossError,
    newStopLossProps?.newUserStopLossPrice ,
    newStopLossProps?.newUserStopLossValue, 
    newStopLossProps?.cancelUserStopLossOrder,
    
    newTrailingProps?.cancelTrailingStopOrder,
    newTrailingProps?.newTrailingStopValue,
    newTrailingProps?.newTrailingStopPercent,
  ])

  // useEffect(() => {
  //   setStopLossError('')
  // }, [newTakeProfitPrice, newStopLossPrice])

  const handleSaveChanges = () => {
    const newOrder = {
      ...order,
      ...newTakeProfitProps,
      ...newStopLossProps,
      ...newTrailingProps,
    }
    updateOrder(newOrder)
    closeModal(true)
  }

  return (
    <OrderUpdateModalCss assetIcon={assetInfo.icon}>
      <div className='popUpContainer tpsl-edit variable-width'>
        <header>
          Edit
          <br />
          Take Profit / Stop Loss
          <div className='closePopUp' onClick={closeModal}>
            X
          </div>
        </header>

        <article>
          <div className='content column'>
            <div className='edit pl_max-min_option'>
              <div className='switch_wrapper'>
                <TakeProfitEdit order={order} onChange={onNewTakeProfitPropsChange} />
                <StopLossEdit order={order} onChange={onNewStopLossPropsChange} />
                <TrailingEdit order={order} onChange={setTrailingProps} />
              </div>
            </div>
          </div>

          <div className='content column action_area'>
            <button
              className={`save_profile ${disabledSaveChanges ? 'disabled' : ''}`}
              disabled={ disabledSaveChanges}
              onClick={handleSaveChanges}
            >
              Save
            </button>
            {(takeProfitError || stopLossError) && <div class='min_error'>{takeProfitError || stopLossError}</div>}
            <button className='cancel_profile' onClick={closeModal}>
              Cancel
            </button>
          </div>
        </article>
      </div>
    </OrderUpdateModalCss>
  )
}